import { Drawer, DrawerOverlay, DrawerProps, useToast } from '@chakra-ui/react';
import {
  usePostPurchaseOrderTransactionMutation,
  usePostPurchaseOrderMutation,
  usePutPurchaseOrderMutation,
} from 'api';
import { useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { PurchaseOrderCreateFormDrawerContent } from './PurchaseOrderCreateFormDrawerContent';
import { PurchaseOrderUpdateFormDrawerContent } from './PurchaseOrderUpdateFormDrawerContent';
import { PurchaseOrderFormReviewDrawerContent } from './PurchaseOrderFormReviewDrawerContent';
import { PurchaseOrderFormRevisionDrawerContent } from './PurchaseOrderFormRevisionDrawerContent';
import { PurchaseOrderResponse } from 'api/bindings/PurchaseOrderResponse';
import { PurchaseOrderSearchRecordResponse } from 'api/bindings/PurchaseOrderSearchRecordResponse';
import { PostPurchaseOrderRequest } from 'api/bindings/PostPurchaseOrderRequest';
import { PutPurchaseOrderRequest } from 'api/bindings/PutPurchaseOrderRequest';
import { PostPurchaseOrderTransactionRequest } from 'api/bindings/PostPurchaseOrderTransactionRequest';

export interface PurchaseOrderFormWorkflowDrawerProps
  extends Omit<DrawerProps, 'onSubmit' | 'children'> {
  po: PurchaseOrderResponse | PurchaseOrderSearchRecordResponse | undefined;
  initialStage?: PurchaseOrderFormWorkflowStage;
}

export enum PurchaseOrderFormWorkflowStage {
  purchaseOrder = 'purchase-order',
  revision = 'revision',
  review = 'review',
}

interface Form {
  currentStage: PurchaseOrderFormWorkflowStage;
  purchaseOrder: PostPurchaseOrderRequest | PutPurchaseOrderRequest;
  revision: PostPurchaseOrderTransactionRequest | undefined;
}

export const PurchaseOrderFormWorkflowDrawer = (
  props: PurchaseOrderFormWorkflowDrawerProps
) => {
  const {
    po,
    initialStage = PurchaseOrderFormWorkflowStage.purchaseOrder,
    ...rest
  } = props;

  // Determine if this is create or update based on whether purchase order has an id
  const isUpdate = po !== undefined;

  // Fetches
  const [postPurchaseOrder] = usePostPurchaseOrderMutation();
  const [putPurchaseOrder] = usePutPurchaseOrderMutation();
  const [postPoRevision] = usePostPurchaseOrderTransactionMutation();

  const { control, getValues, setValue, handleSubmit, watch } = useForm<Form>({
    mode: 'all',
    defaultValues: {
      currentStage: initialStage,
      purchaseOrder: po as any,
    },
  });

  // const formValues = watch();
  const [poForm, revision, currentStage] = watch([
    'purchaseOrder',
    'revision',
    'currentStage',
  ]);

  const toast = useToast();
  const afeId = useMemo(() => {
    if (po) {
      return po.afeId;
    } else if ('id' in poForm) {
      return poForm.id;
    } else {
      throw Error('could not find afeId to pass');
    }
  }, [po, poForm]);

  const onSubmit = useCallback(
    async (form: Form) => {
      try {
        let poId = po !== undefined ? po.id : undefined;

        if (initialStage === PurchaseOrderFormWorkflowStage.purchaseOrder) {
          let result;
          if (isUpdate) {
            result = await putPurchaseOrder(form.purchaseOrder as any); // form.purchaseOrder will be PutPurchaseOrderRequest
          } else {
            result = await postPurchaseOrder(form.purchaseOrder as any); // form.purchaseOrder will be PostPurchaseOrderRequest
          }

          if ('error' in result) {
            toast({
              title: `Failed to post purchase order ${form.purchaseOrder.name}`,
              status: 'error',
            });
            throw result.error;
          }

          // poId = result.data.id;
          if (!poId) {
            poId = result.data.id as string;
          }

          toast({
            title: `Purchase Order ${form.purchaseOrder.name} posted!`,
            status: 'success',
          });
        }
        if (revision) {
          // formValues.revision.purchaseOrderId = poId;
          const result = await postPoRevision({
            entityId: poId!,
            ...revision,
            userId: null as any,
          });
          if ('error' in result) {
            toast({
              title: `Failed to post revision ${form.revision?.name} to purchase order ${form.purchaseOrder.name}`,
              status: 'error',
            });
            throw result.error;
          }

          toast({
            title: `Revision ${form.revision?.name} posted to purchase order ${form.purchaseOrder.name}`,
            status: 'success',
          });
        }

        props.onClose();
      } catch {
        // TODO
      }
    },
    [
      po,
      initialStage,
      revision,
      props,
      isUpdate,
      toast,
      putPurchaseOrder,
      postPurchaseOrder,
      postPoRevision,
    ]
  );
  const component = useMemo(() => {
    switch (currentStage) {
      case PurchaseOrderFormWorkflowStage.purchaseOrder:
        return (
          <Controller
            control={control}
            name="purchaseOrder"
            render={(p) => {
              if (isUpdate) {
                return (
                  <PurchaseOrderUpdateFormDrawerContent
                    purchaseOrder={po}
                    onCancel={props.onClose}
                    onSubmit={(f) => {
                      p.field.onChange(f);
                      const nextStage = f.includeRevision
                        ? PurchaseOrderFormWorkflowStage.revision
                        : PurchaseOrderFormWorkflowStage.review;
                      setValue('currentStage', nextStage);
                    }}
                  />
                );
              } else {
                return (
                  <PurchaseOrderCreateFormDrawerContent
                    onCancel={props.onClose}
                    onSubmit={(f) => {
                      p.field.onChange(f);
                      const nextStage = f.includeRevision
                        ? PurchaseOrderFormWorkflowStage.revision
                        : PurchaseOrderFormWorkflowStage.review;
                      setValue('currentStage', nextStage);
                    }}
                  />
                );
              }
            }}
          />
        );
      case PurchaseOrderFormWorkflowStage.revision:
        return (
          <Controller
            control={control}
            name="revision"
            render={(p) => (
              <PurchaseOrderFormRevisionDrawerContent
                po={poForm}
                onClose={props.onClose}
                onSubmit={(f) => {
                  p.field.onChange(f);
                  setValue(
                    'currentStage',
                    PurchaseOrderFormWorkflowStage.review
                  );
                }}
              />
            )}
          />
        );
      case PurchaseOrderFormWorkflowStage.review:
        return (
          <PurchaseOrderFormReviewDrawerContent
            afeId={afeId}
            po={poForm}
            revision={revision}
            onCancel={props.onClose}
            onSubmit={() => handleSubmit(onSubmit)()}
          />
        );
      default:
        return <></>;
    }
  }, [
    currentStage,
    control,
    afeId,
    poForm,
    revision,
    props.onClose,
    isUpdate,
    po,
    setValue,
    handleSubmit,
    onSubmit,
  ]);

  return (
    <Drawer size="xl" {...rest}>
      <DrawerOverlay />
      {component}
    </Drawer>
  );
};
